import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Store, Plus, Trash2, KeyRound, Bell, Lock, MessageCircle, History, FileSpreadsheet } from "lucide-react";
import { StoreLoginsDialog } from "@/components/StoreLoginsDialog";
import { StoresImportDialog } from "@/components/StoresImportDialog";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";
import Logo from "@/components/Logo";

export default function StoresAdmin() {
  const navigate = useNavigate();
  const [stores, setStores] = useState([]);
  const [form, setForm] = useState({ name: "", pin: "" });
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loginsFor, setLoginsFor] = useState(null);
  const [importOpen, setImportOpen] = useState(false);

  const load = useCallback(() => api.listStores().then(setStores), []); // api è un modulo, setStores è stabile
  useEffect(() => {
    load();
  }, [load]);

  const create = async () => {
    setBusy(true);
    try {
      await api.createStore(form);
      toast.success("Sede creata");
      setForm({ name: "", pin: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nella creazione");
    } finally {
      setBusy(false);
    }
  };

  const unlock = async (s) => {
    try {
      await api.unlockStore(s.id);
      toast.success(`${s.name} sbloccata: può accedere subito`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sblocco non riuscito");
    }
  };
  const tempPin = async (s) => {
    if (!window.confirm(`Inviare un PIN temporaneo via WhatsApp a ${s.phone}? Il PIN attuale di "${s.name}" verrà sostituito e la sede dovrà sceglierne uno nuovo al primo accesso.`)) return;
    try {
      const r = await api.sendTempPin(s.id);
      toast.success(`PIN temporaneo inviato via WhatsApp a ${r.phone}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invio non riuscito");
    }
  };

  const changePin = async (s) => {
    const pin = window.prompt(`Nuovo PIN per "${s.name}" (6-8 cifre, niente sequenze):`);
    if (pin == null) return;
    try {
      await api.updateStorePin(s.id, pin.trim());
      toast.success("PIN aggiornato");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Eliminare la sede "${s.name}"?`)) return;
    await api.deleteStore(s.id);
    toast.success("Sede eliminata");
    load();
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-3">
          <Button data-testid="stores-back-button" variant="ghost" size="icon" onClick={() => navigate("/")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <Logo className="h-10" />
          <div>
            <h1 className="font-display font-bold text-xl">Punti Vendita</h1>
            <p className="text-xs text-muted-foreground">Sedi abilitate al ritiro premi con PIN personale</p>
          </div>
          <Button data-testid="stores-import-button" variant="outline" size="sm" className="ml-auto gap-1.5" onClick={() => setImportOpen(true)}><FileSpreadsheet className="h-4 w-4" /> Importa / Esporta Excel</Button>
        </div>
      </header>
      <StoresImportDialog open={importOpen} onOpenChange={setImportOpen} onDone={load} />

      <main className="max-w-4xl mx-auto px-6 py-8 grid lg:grid-cols-5 gap-6" data-testid="stores-page">
        <Card className="lg:col-span-2 p-5 bg-card border-border h-fit space-y-3">
          <h3 className="font-display font-bold text-lg flex items-center gap-2">
            <Plus className="h-4 w-4" /> Nuova sede
          </h3>
          <div className="space-y-1.5">
            <Label>Nome punto vendita</Label>
            <Input
              required
              data-testid="store-name-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Es. Store Milano Centro"
            />
          </div>
          <div className="space-y-1.5">
            <Label>PIN (6-8 cifre)</Label>
            <Input
              required
              data-testid="store-pin-input"
              type="password"
              inputMode="numeric"
              value={form.pin}
              onChange={(e) => setForm({ ...form, pin: e.target.value })}
              placeholder="••••"
            />
          </div>
          <Button data-testid="create-store-button" onClick={create} disabled={busy} className="w-full gap-2">
            <Plus className="h-4 w-4" /> Crea sede
          </Button>
          <p className="text-xs text-muted-foreground">
            Il personale userà questo PIN nella pagina Verifica Ricevuta per registrare i ritiri.
          </p>
        </Card>

        <div className="lg:col-span-3 space-y-3">
          {stores.length === 0 ? (
            <Card className="p-10 text-center border-dashed border-2 bg-card/50">
              <Store className="h-10 w-10 mx-auto text-muted-foreground/40" />
              <p className="text-muted-foreground mt-3">Nessuna sede configurata.</p>
            </Card>
          ) : (
            stores.map((s) => (
              <Card key={s.id} data-testid={`store-row-${s.id}`} className="p-4 bg-card border-border flex flex-col sm:flex-row sm:items-start gap-4">
                <div className="h-12 w-12 rounded-xl bg-secondary grid place-items-center shrink-0">
                  <Store className="h-6 w-6 text-primary" />
                </div>
                <div className="flex-1 min-w-0 space-y-0.5">
                  <h4 className="font-semibold truncate">{s.name}</h4>
                  <p className="text-xs text-muted-foreground">PIN impostato · creata il {new Date(s.created_at).toLocaleDateString("it-IT")}</p>
                  <p className="text-xs mt-0.5" data-testid={`store-alert-info-${s.id}`}>
                    {s.email ? <span className="text-emerald-400">Avvisi a {s.email} · soglia {s.alert_threshold_pct ?? 20}%</span> : <span className="text-amber-400">Nessuna email per avvisi montepremi</span>}
                    {s.phone && <span className="text-emerald-400 ml-2" data-testid="store-phone-label">· WhatsApp {s.phone}</span>}
                  </p>
                  {s.wrong_store_alerts_month?.count > 0 && (
                    <p className="text-xs mt-0.5 text-red-400" data-testid={`store-wrong-alerts-${s.id}`}>
                      Fuori sede: soglia superata {s.wrong_store_alerts_month.count} {s.wrong_store_alerts_month.count === 1 ? "volta" : "volte"} questo mese
                      {" "}({s.wrong_store_alerts_month.attempts} tentativi · ultimo {new Date(s.wrong_store_alerts_month.last_date).toLocaleDateString("it-IT")})
                    </p>
                  )}
                  {s.code_alerts?.count > 0 && (
                    <p className="text-xs mt-0.5 text-amber-400" data-testid={`store-code-alerts-${s.id}`}>
                      Codici in esaurimento: avvisata {s.code_alerts.count} {s.code_alerts.count === 1 ? "volta" : "volte"} su {s.code_alerts.contests} concors{s.code_alerts.contests === 1 ? "o" : "i"} · ultimo {new Date(s.code_alerts.last).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })} ({s.code_alerts.last_left}/{s.code_alerts.last_total} rimasti)
                    </p>
                  )}
                  {s.locked_until && (
                    <p className="text-xs mt-0.5 text-red-400 flex items-center gap-2" data-testid={`store-locked-${s.id}`}>
                      <Lock className="h-3 w-3" /> Bloccata per PIN errati fino alle {new Date(s.locked_until).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
                      <button type="button" onClick={() => unlock(s)} className="underline text-emerald-400" data-testid={`store-unlock-${s.id}`}>Sblocca ora</button>
                    </p>
                  )}
                  {s.must_change_pin && <p className="text-xs mt-0.5 text-amber-400" data-testid={`store-must-change-${s.id}`}>PIN temporaneo o vecchio: al prossimo accesso la sede dovrà sceglierne uno nuovo</p>}
                  <div className="flex flex-wrap items-center gap-2 pt-2" data-testid={`store-actions-${s.id}`}>
                <Button data-testid={`store-notify-${s.id}`} variant="secondary" size="sm" onClick={() => setEditing(s)} className="gap-1.5">
                  <Bell className="h-3.5 w-3.5" /> Notifiche
                </Button>
                <Button data-testid={`store-logins-${s.id}`} variant="secondary" size="sm" onClick={() => setLoginsFor(s)} className="gap-1.5">
                  <History className="h-3.5 w-3.5" /> Accessi
                </Button>
                <Button data-testid={`store-pin-${s.id}`} variant="secondary" size="sm" onClick={() => changePin(s)} className="gap-1.5">
                  <KeyRound className="h-3.5 w-3.5" /> Cambia PIN
                </Button>
                <Button data-testid={`store-temp-pin-${s.id}`} variant="outline" size="sm" onClick={() => tempPin(s)} disabled={!s.phone} title={s.phone ? `Invia un PIN temporaneo via WhatsApp a ${s.phone}` : "Imposta prima il numero WhatsApp in Notifiche"} className="gap-1.5 border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10">
                  <MessageCircle className="h-3.5 w-3.5" /> PIN dimenticato
                </Button>
                <Button data-testid={`store-delete-${s.id}`} variant="ghost" size="icon" onClick={() => remove(s)} className="text-muted-foreground hover:text-destructive">
                  <Trash2 className="h-4 w-4" />
                </Button>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      </main>
      <StoreNotifyDialog store={editing} onClose={() => setEditing(null)} onSaved={load} />
      <StoreLoginsDialog store={loginsFor} onClose={() => setLoginsFor(null)} />
    </div>
  );
}

function StoreNotifyDialog({ store, onClose, onSaved }) {
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [weekly, setWeekly] = useState(true);
  const [pct, setPct] = useState(20);
  const [codesPct, setCodesPct] = useState(10);
  const [addr, setAddr] = useState({ brand: "", address: "", zip_code: "", city: "", province: "", vat_id: "" });
  useEffect(() => {
    if (store) {
      setEmail(store.email || "");
      setPhone(store.phone || "");
      setWeekly(store.weekly_rank_whatsapp !== false);
      setPct(store.alert_threshold_pct ?? 20);
      setCodesPct(store.codes_alert_pct ?? 10);
      setAddr({ brand: store.brand || "", address: store.address || "", zip_code: store.zip_code || "", city: store.city || "", province: store.province || "", vat_id: store.vat_id || "" });
    }
  }, [store]); // eslint-disable-line react-hooks/exhaustive-deps -- sincronizza il form quando cambia la sede; i setter di useState sono stabili
  const save = async () => {
    try {
      await api.updateStore(store.id, { email: email.trim() || null, phone: phone.trim() || null, weekly_rank_whatsapp: weekly, alert_threshold_pct: Number(pct), codes_alert_pct: Number(codesPct), ...addr });
      toast.success("Dati sede aggiornati");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Errore");
    }
  };
  return (
    <Dialog open={!!store} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="store-notify-dialog">
        <DialogHeader>
          <DialogTitle>Dati e notifiche sede · {store?.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Email del punto vendita</Label>
            <Input data-testid="store-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="negozio@esempio.it" />
          </div>
          <div className="space-y-1.5">
            <Label>Telefono WhatsApp della sede</Label>
            <Input data-testid="store-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+39 333 1234567" />
          </div>
          <div className="rounded-md border border-border p-3 space-y-2">
            <p className="text-xs font-semibold">Dati per il riconoscimento OCR degli scontrini</p>
            <div className="grid gap-2 sm:grid-cols-2">
              <Input data-testid="store-brand-input" value={addr.brand} onChange={(e) => setAddr({ ...addr, brand: e.target.value })} placeholder="Insegna (es. Conad City)" />
              <Input data-testid="store-vat-input" value={addr.vat_id} onChange={(e) => setAddr({ ...addr, vat_id: e.target.value })} placeholder="P.IVA (11 cifre)" />
              <Input data-testid="store-address-input" className="sm:col-span-2" value={addr.address} onChange={(e) => setAddr({ ...addr, address: e.target.value })} placeholder="Indirizzo (via e civico)" />
              <Input data-testid="store-zip-input" value={addr.zip_code} onChange={(e) => setAddr({ ...addr, zip_code: e.target.value })} placeholder="CAP" />
              <div className="grid grid-cols-[1fr_64px] gap-2">
                <Input data-testid="store-city-input" value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} placeholder="Città" />
                <Input data-testid="store-province-input" value={addr.province} onChange={(e) => setAddr({ ...addr, province: e.target.value.toUpperCase() })} placeholder="PR" maxLength={2} />
              </div>
            </div>
          </div>
          <label className="flex items-center justify-between gap-3 rounded-md bg-secondary/40 px-3 py-2 text-sm">
            <span>Classifica settimanale via WhatsApp (lunedì 9:00)</span>
            <input type="checkbox" data-testid="store-weekly-rank-toggle" checked={weekly} onChange={(e) => setWeekly(e.target.checked)} className="h-4 w-4 accent-primary" />
          </label>
          <div className="space-y-1.5">
            <Label>Avvisa quando i premi rimasti oggi sono ≤ {pct}% del montepremi giornaliero</Label>
            <Input data-testid="store-threshold-input" type="number" min={1} max={100} value={pct} onChange={(e) => setPct(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Avvisa su WhatsApp quando i codici non usati scendono sotto il {codesPct}% (concorsi attivi)</Label>
            <Input data-testid="store-codes-threshold-input" type="number" min={1} max={100} value={codesPct} onChange={(e) => setCodesPct(e.target.value)} />
          </div>
          <p className="text-xs text-muted-foreground">
            L'avviso viene inviato una sola volta al giorno per ogni premio, solo per i premi con quantità giornaliera nel montepremi della sede.
          </p>
          <Button data-testid="store-notify-save" onClick={save} className="w-full">Salva</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
